import { useState, useEffect, useRef, type ReactNode } from 'react';
import { Copy, Check, Save, Pencil, X, Loader as Loader2, SlidersHorizontal, TriangleAlert as AlertTriangle, Maximize2, RefreshCw } from 'lucide-react';
import FormattedText from '../../common/FormattedText';

const WORD_PRESETS: { label: string; min: number; max: number }[] = [
  { label: 'Concise', min: 30, max: 60 },
  { label: 'Standard', min: 60, max: 120 },
  { label: 'Detailed', min: 120, max: 200 },
];

interface Props {
  title: string;
  icon: ReactNode;
  fieldKey: string;
  content: string;
  headerColor: string;
  cardColor: string;
  onSave: (fieldKey: string, value: string) => Promise<void>;
  hasUnsavedChanges: (fieldKey: string, dirty: boolean) => void;
  showWordControls?: boolean;
  onAdjustToWordRange?: (fieldKey: string, minWords: number, maxWords: number) => void;
  adjustingWords?: boolean;
  charLimit?: number;
  expandable?: boolean;
  onRegenerate?: (fieldKey: string) => void;
  regenerating?: boolean;
}

export default function EditableOutreachCard({
  title,
  icon,
  fieldKey,
  content,
  headerColor,
  cardColor,
  onSave,
  hasUnsavedChanges,
  showWordControls,
  onAdjustToWordRange,
  adjustingWords,
  charLimit,
  expandable,
  onRegenerate,
  regenerating,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [draft, setDraft] = useState(content);
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [showRangePanel, setShowRangePanel] = useState(false);
  const [minWords, setMinWords] = useState(60);
  const [maxWords, setMaxWords] = useState(120);

  useEffect(() => {
    setDraft(content);
    setEditing(false);
  }, [content]);

  useEffect(() => {
    hasUnsavedChanges(fieldKey, editing && draft !== content);
  }, [editing, draft, content, fieldKey, hasUnsavedChanges]);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  }, [editing, draft]);

  if (!content && !editing) return null;

  const wordCount = content ? content.trim().split(/\s+/).filter(Boolean).length : 0;
  const charCount = content ? content.length : 0;
  const overCharLimit = charLimit && charCount > charLimit;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(fieldKey, draft);
    setSaving(false);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft(content);
    setEditing(false);
  };

  const applyPreset = (preset: typeof WORD_PRESETS[number]) => {
    setMinWords(preset.min);
    setMaxWords(preset.max);
  };

  const handleApplyRange = () => {
    if (onAdjustToWordRange && minWords > 0 && maxWords > minWords) {
      onAdjustToWordRange(fieldKey, minWords, maxWords);
    }
  };

  const isDirty = draft !== content;

  return (
    <div className={`rounded-lg border shadow-none overflow-hidden ${cardColor}`}>
      <div className={`${headerColor} px-5 py-3 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          {icon}
          <h4 className="text-sm font-semibold text-white">{title}</h4>
          {content && (
            <span className="ml-1 text-[10px] font-medium text-white/60 bg-app-surface/10 px-1.5 py-0.5 rounded">
              {wordCount} words
            </span>
          )}
          {overCharLimit && (
            <span className="ml-1 flex items-center gap-0.5 text-[10px] font-medium text-app-warning bg-app-warning/30 px-1.5 py-0.5 rounded">
              <AlertTriangle className="w-2.5 h-2.5" />
              {charCount}/{charLimit} chars
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {!editing && (
            <>
              {onRegenerate && (
                <button
                  onClick={() => onRegenerate(fieldKey)}
                  disabled={regenerating}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors disabled:opacity-40"
                >
                  {regenerating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                  {regenerating ? 'Generating...' : 'Regenerate'}
                </button>
              )}
              {showWordControls && onAdjustToWordRange && (
                <button
                  onClick={() => setShowRangePanel(prev => !prev)}
                  disabled={adjustingWords || !content}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors disabled:opacity-40"
                >
                  {adjustingWords ? <Loader2 className="w-3 h-3 animate-spin" /> : <SlidersHorizontal className="w-3 h-3" />}
                  Adjust
                </button>
              )}
              {expandable && (
                <button
                  onClick={() => setModalOpen(true)}
                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
                >
                  <Maximize2 className="w-3 h-3" />
                  Expand
                </button>
              )}
              <button
                onClick={() => { setDraft(content); setEditing(true); }}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Edit
              </button>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </>
          )}
          {editing && (
            <>
              <button
                onClick={handleCancel}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
              >
                <X className="w-3 h-3" />
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !isDirty}
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-white bg-app-surface/20 hover:bg-app-surface/30 rounded transition-colors disabled:opacity-40"
              >
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                Save
              </button>
            </>
          )}
        </div>
      </div>

      {showRangePanel && !editing && showWordControls && (
        <div className="px-5 py-3 bg-app-surface/80 border-b border-app-line flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-1.5">
            {WORD_PRESETS.map(preset => (
              <button
                key={preset.label}
                onClick={() => applyPreset(preset)}
                className={`px-2 py-1 text-[10px] font-medium rounded-md border transition-colors ${
                  minWords === preset.min && maxWords === preset.max
                    ? 'bg-app-ink text-white border-forest-700'
                    : 'bg-app-surface text-app-muted border-app-line-strong hover:border-app-line-strong'
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1.5 text-[10px] text-app-muted">
            <span>Min</span>
            <input
              type="number"
              value={minWords}
              onChange={e => setMinWords(Number(e.target.value))}
              className="w-14 px-1.5 py-1 text-[10px] border border-app-line-strong rounded text-center focus:outline-none focus:ring-1 focus:ring-forest-400"
              min={10}
              max={maxWords - 1}
            />
            <span>Max</span>
            <input
              type="number"
              value={maxWords}
              onChange={e => setMaxWords(Number(e.target.value))}
              className="w-14 px-1.5 py-1 text-[10px] border border-app-line-strong rounded text-center focus:outline-none focus:ring-1 focus:ring-forest-400"
              min={minWords + 1}
              max={500}
            />
          </div>
          <button
            onClick={handleApplyRange}
            disabled={adjustingWords || minWords >= maxWords}
            className="px-3 py-1 text-[10px] font-semibold bg-forest-600 text-white rounded-md hover:bg-forest-700 transition-colors disabled:opacity-40"
          >
            {adjustingWords ? 'Adjusting...' : 'Apply'}
          </button>
        </div>
      )}

      <div className="p-5">
        {editing ? (
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={e => setDraft(e.target.value)}
            className="w-full text-sm text-app-ink leading-relaxed bg-app-surface border border-app-line-strong rounded-lg p-3 focus:outline-none focus:ring-2 focus:ring-forest-300 focus:border-forest-400 resize-none transition-colors"
            rows={4}
          />
        ) : (
          <FormattedText text={content} className="text-sm text-app-ink" />
        )}
      </div>

      {expandable && modalOpen && (
        <ExpandModal
          title={title}
          icon={icon}
          content={content}
          headerColor={headerColor}
          onClose={() => setModalOpen(false)}
        />
      )}
    </div>
  );
}

function ExpandModal({ title, icon, content, headerColor, onClose }: {
  title: string;
  icon: ReactNode;
  content: string;
  headerColor: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-app-surface rounded-lg shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto mx-4">
        <div className={`sticky top-0 ${headerColor} px-6 py-4 flex items-center justify-between rounded-t-2xl z-10`}>
          <div className="flex items-center gap-2">
            {icon}
            <h3 className="text-base font-semibold text-white">{title}</h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleCopy}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-white/80 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
            >
              {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              {copied ? 'Copied' : 'Copy All'}
            </button>
            <button
              onClick={onClose}
              className="p-1.5 text-white/70 hover:text-white hover:bg-app-surface/10 rounded transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-8">
          <FormattedText text={content} className="text-base text-app-ink leading-relaxed" large />
        </div>
      </div>
    </div>
  );
}
